import { QuoteCardStage } from '@/components/quotes/quoteCardTypes'

/** How reliably a field or stage shows up. Drives the badge on each cell. */
export enum Guarantee {
  ALWAYS = 'always',
  SOMETIMES = 'sometimes',
  NEVER = 'never',
}

/** The three ways an offer reaches a card, which is what changes the guarantees. */
export enum Configuration {
  CARRIER = 'carrier',
  WHOLESALER_AUTO = 'wholesaler_auto',
  WHOLESALER_MANUAL = 'wholesaler_manual',
}

export interface ConfigurationSpec {
  key: Configuration
  label: string
  vendors: string
  /** Whether a card is on the page at first paint, before any vendor round-trip. */
  cardAtFirstPaint: Guarantee
  cardAtFirstPaintNote: string
  carrierName: string
  brokerValue: string
  /** Whether the card's name changes from wholesaler to carrier partway through. */
  nameSwap: string
  happyPath: string
  tail: string
}

export const CONFIGURATIONS: ConfigurationSpec[] = [
  {
    key: Configuration.CARRIER,
    label: 'Carrier, direct',
    vendors: 'CoverWhale, Corgi, Nirvana, GEICO',
    cardAtFirstPaint: Guarantee.ALWAYS,
    cardAtFirstPaintNote: 'ensure_pending_indication runs before the dispatch is enqueued, inside confirm',
    carrierName: "The vendor's own name",
    brokerValue: 'null',
    nameSwap: 'Never — no wholesaler involved',
    happyPath: 'Corgi 2 min budget, Nirvana 3 min ceiling, CoverWhale 6 min budget',
    tail: 'GEICO only: a dropped Supergood callback is closed out by a 10-minute sweep',
  },
  {
    key: Configuration.WHOLESALER_AUTO,
    label: 'Wholesaler, auto',
    vendors: 'Rocklake — auto liability, physdam, cargo',
    cardAtFirstPaint: Guarantee.NEVER,
    cardAtFirstPaintNote: 'rows are created inside the task, after submit_quote returns',
    carrierName: 'The assigned carrier, e.g. Progressive',
    brokerValue: '"Rocklake", from card creation',
    nameSwap: 'Never — the carrier is known before the card exists',
    happyPath: '~70s: submit, then 10s delay, then 12 polls at 5s',
    tail: '30-min cron, tiering 30m → 2h → 6h → 12h, up to 96h auto / 48h physdam and cargo',
  },
  {
    key: Configuration.WHOLESALER_MANUAL,
    label: 'Wholesaler, manual',
    vendors: 'Amwins, Ryan Specialty, and staged Rocklake',
    cardAtFirstPaint: Guarantee.NEVER,
    cardAtFirstPaintNote: 'no integration exists — a person creates the row in Console',
    carrierName: 'The wholesaler, until staff assign a carrier',
    brokerValue: 'The same wholesaler name, so it can duplicate',
    nameSwap: 'Yes — this is the only configuration where it happens',
    happyPath: 'None. Human-paced, and can land days later',
    tail: 'Staged Rocklake parks on portal page 8 awaiting staff QA, unbounded',
  },
]

export interface StageRow {
  stage: QuoteCardStage
  /** The backend status this stage is derived from. */
  from: string
  minimal: boolean
  reach: Record<Configuration, Guarantee>
  note: string
}

export const STAGE_ROWS: StageRow[] = [
  {
    stage: QuoteCardStage.PREPARING,
    from: 'PENDING with no dateSubmitted',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_AUTO]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.ALWAYS,
    },
    note: 'The window between dispatch and the carrier accepting the submission. Staged wholesaler rows sit here until staff submit the final portal page.',
  },
  {
    stage: QuoteCardStage.APPLICATION_SENT,
    from: 'PENDING with dateSubmitted set',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_AUTO]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.SOMETIMES,
    },
    note: 'dateSubmitted is stamped when the application actually goes out, so this now renders during quoting, before any price exists.',
  },
  {
    stage: QuoteCardStage.PRICED,
    from: 'GENERATED, CANCELED, a QUOTED quote, or our own estimate',
    minimal: false,
    reach: {
      [Configuration.CARRIER]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_AUTO]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.ALWAYS,
    },
    note: 'The only stage carrying coverages and pricing. Everything else falls back to the minimal card.',
  },
  {
    stage: QuoteCardStage.DECLINED,
    from: 'DECLINED',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_AUTO]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.SOMETIMES,
    },
    note: 'Greyscale icon. Rocklake sets this when a carrier row comes back declined.',
  },
  {
    stage: QuoteCardStage.ERROR,
    from: 'ERROR',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_AUTO]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.SOMETIMES,
    },
    note: 'A technical failure, distinct from declined. Rocklake writes one error row when the whole submit fails.',
  },
  {
    stage: QuoteCardStage.UNAVAILABLE,
    from: 'UNAVAILABLE',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.ALWAYS,
      [Configuration.WHOLESALER_AUTO]: Guarantee.NEVER,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.NEVER,
    },
    note: 'Written up front by a state-eligibility check, which only CoverWhale and Nirvana run.',
  },
  {
    stage: QuoteCardStage.REQUEST_TO_BIND,
    from: 'A REQUEST_TO_BIND quote',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_AUTO]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.SOMETIMES,
    },
    note: 'Quote-only, so it needs a real quote rather than an indication.',
  },
  {
    stage: QuoteCardStage.BOUND,
    from: 'A BOUND quote',
    minimal: true,
    reach: {
      [Configuration.CARRIER]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_AUTO]: Guarantee.SOMETIMES,
      [Configuration.WHOLESALER_MANUAL]: Guarantee.SOMETIMES,
    },
    note: 'No distinct treatment yet — currently renders as a minimal card.',
  },
]

export interface FieldRow {
  field: string
  minimal: Guarantee
  priced: Guarantee
  note: string
}

/** What the card can rely on, split by the two shapes in quoteCardTypes. */
export const FIELD_ROWS: FieldRow[] = [
  {
    field: 'carrierName, iconUrl, iconAlt',
    minimal: Guarantee.ALWAYS,
    priced: Guarantee.ALWAYS,
    note: 'On QuoteCardBase. Always safe to render, at every stage.',
  },
  {
    field: 'stage',
    minimal: Guarantee.ALWAYS,
    priced: Guarantee.ALWAYS,
    note: 'Derived, never absent.',
  },
  {
    field: 'carrierRating, coiSpeed',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Omitted rather than guessed when the carrier profile has no sourced value.',
  },
  {
    field: 'statusMessage',
    minimal: Guarantee.NEVER,
    priced: Guarantee.NEVER,
    note: 'Designed, not implemented — the mapper never sets it.',
  },
  {
    field: 'needsAttestation',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Can be set on either shape. A priced card shows the prompt through indicationStatus.',
  },
  {
    field: 'outstandingDocuments',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'One upload prompt per document. On a priced card it also blocks the cart.',
  },
  {
    field: 'carrierProgram',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Set when one brand quotes on several papers.',
  },
  {
    field: 'failureReason',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Only on a card showing a failure the failure analysis has explained. Replaces that stage line.',
  },
  {
    field: 'coverages',
    minimal: Guarantee.NEVER,
    priced: Guarantee.ALWAYS,
    note: 'The reason PRICED is a separate shape.',
  },
  {
    field: 'pricingKind, quotedAt',
    minimal: Guarantee.NEVER,
    priced: Guarantee.ALWAYS,
    note: 'quotedAt is set only when the card is backed by a quote, not an indication.',
  },
  {
    field: 'brokerNote, brokerName',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: 'brokerName is the assigned Cai broker — a person, unrelated to the wholesaler.',
  },
  {
    field: 'financing, claimsCapabilities',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: 'Carrier-profile sourced, so independently absent. Financing always maps as standard.',
  },
  {
    field: 'coverage.aggregateLimit',
    minimal: Guarantee.NEVER,
    priced: Guarantee.NEVER,
    note: 'Designed, not implemented — no carrier integration sends an aggregate yet.',
  },
  {
    field: 'financing.plans',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: "GEICO only: its plans for the 12-month policy, read off its quote, in place of the record's text.",
  },
  {
    field: 'moratorium',
    minimal: Guarantee.NEVER,
    priced: Guarantee.NEVER,
    note: 'Designed, not implemented — the mapper never sets it.',
  },
  {
    field: 'isEstimateOnly, failedStage',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: 'Our own estimate. failedStage is set when it stands in front of a failed submission.',
  },
  {
    field: 'totalPremiumRange, coverages[].premiumRange',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: "A Corgi prequote: its auto liability runs 10% under to 60% over Corgi's figure, and the total with it, each end rounded up to the next $100.",
  },
  {
    field: 'supersededAt',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: 'A canceled round a re-quote replaced. Still checkoutable.',
  },
  {
    field: 'memberIds, coverageOutcomes',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Set when one carrier answers on several lines. coverageOutcomes only on a card that priced something.',
  },
  {
    field: 'quoteDocuments',
    minimal: Guarantee.NEVER,
    priced: Guarantee.SOMETIMES,
    note: 'Present once a source document exists. The timestamp is a dateUpdated proxy, not a real document date.',
  },
  {
    field: 'sourcedThrough',
    minimal: Guarantee.SOMETIMES,
    priced: Guarantee.SOMETIMES,
    note: 'Rocklake only, when the same brand also came direct from Progressive.',
  },
]
